import Button from '@/components/Button'
import Layout, { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import ToggleSwitch from '@/components/ToggleSwitch'
import { getConfig } from '@/lib/config'
import { isValidReturnTo } from '@/lib/returnToUrl'
import { PricingPlanAction, PricingPlanActionType, PricingPlanPricing } from '@/shared/api-generated-types'
import { useRouter } from 'next/router'
import { useState } from 'react'

export interface Props {
  pricing: PricingPlanPricing[]
  primaryAction?: PricingPlanAction
}

const {
  publicRuntimeConfig: {
    website: { url: website_base_url },
  },
} = getConfig()

const monthLabel = 'monthly'
const annualLabel = 'annually'

function intervalToLabel(interval: string): string | undefined {
  return interval === 'month' ? monthLabel : interval === 'year' ? annualLabel : undefined
}

function composeReturnToUrl(returnTo?: string): string {
  if (returnTo && isValidReturnTo(returnTo)) {
    return returnTo
  }

  return new URL('pricing', website_base_url).toString()
}

const TogglePricingVariation = ({ pricing, primaryAction }: Props): JSX.Element | null => {
  const router = useRouter()
  const [priceIndex, setPriceIndex] = useState<number>(0)

  if (!pricing.length) return null

  if (pricing && pricing.length > 2) {
    console.error(`There's an unexpected number of pricing variations`, { pricing })
    return null
  }

  const singlePrice = pricing.length === 1

  const price = pricing[priceIndex]

  const returnToUrl: string = composeReturnToUrl(router?.query?.returnTo ? String(router.query.returnTo) : undefined)

  return (
    <>
      <div className="pt-sm" style={{ flexGrow: 1 }}>
        {!singlePrice ? (
          <div>
            <Layout spacing="sm">
              <Stack x>
                <div onClick={() => setPriceIndex(0)} style={{ cursor: 'pointer' }}>
                  {pricing[0].billingInterval ? (
                    <Text size="xs" weight="sm">
                      Bill {intervalToLabel(pricing[0].billingInterval)}
                    </Text>
                  ) : null}
                </div>
                <div data-testid="price-toggle">
                  <ToggleSwitch
                    id="price-toggle"
                    name="price-toggle"
                    checked={Boolean(priceIndex)}
                    onChange={(checked) => {
                      if (checked) setPriceIndex(1)
                      else setPriceIndex(0)
                    }}
                    optionLabels={['', '']}
                  />
                </div>
                <div onClick={() => setPriceIndex(1)} style={{ cursor: 'pointer' }}>
                  {pricing[1].billingInterval ? (
                    <Text size="xs" weight="sm">
                      Bill {intervalToLabel(pricing[1].billingInterval)}
                    </Text>
                  ) : null}
                </div>
              </Stack>
            </Layout>
          </div>
        ) : null}
        <div className="pt-sm">
          {price.displayPrice ? (
            <Text tag="span" size="lg" weight="lg" className="pb-sm">
              {price.displayPrice}
            </Text>
          ) : null}
          {price.billingInterval ? (
            <Text tag="span" size="sm" weight="sm" className="pb-sm">
              /{price.billingInterval}
            </Text>
          ) : null}
        </div>
      </div>
      <div>
        {primaryAction && primaryAction.type === PricingPlanActionType.Form && primaryAction.url ? (
          <div>
            <form action={primaryAction.url} method="post" target={`_${primaryAction.target}`}>
              <input type="hidden" name="returnUrl" value={returnToUrl} />
              <input type="hidden" name="gsuPriceId" value={pricing[priceIndex].gsuPriceId} />
              <Button component="submit" size="md" theme="primary" label={primaryAction.label} />
            </form>
          </div>
        ) : null}
        {primaryAction && primaryAction.type === PricingPlanActionType.SelectedForm && primaryAction.url ? (
          <div>
            <form action={primaryAction.url} method="post" target={`_${primaryAction.target}`}>
              <input type="hidden" name="returnUrl" value={returnToUrl} />
              <input type="hidden" name="gsuPriceId" value={pricing[priceIndex].gsuPriceId} />
              <Button component="submit" size="md" theme="green" label={primaryAction.label} />
            </form>
          </div>
        ) : null}
      </div>
    </>
  )
}
export default TogglePricingVariation
